import { Boton } from "../../components/ui/primitivos";
import type { ContactoCliente } from "../../lib/consola-api";

/**
 * Los contactos del cliente, editables en bloque.
 *
 * Se manda la **lista final** y no una diferencia, igual que los renglones de un
 * presupuesto (ADR 0028): el worker reemplaza. Por eso acá se edita en memoria y
 * recién se persiste al guardar la ficha.
 *
 * **Exactamente uno es el predeterminado** — es a quien se le manda el
 * presupuesto—. Marcarlo desmarca al otro, en vez de dejar que queden dos o
 * ninguno y que el servidor elija por su cuenta.
 */
export function SeccionContactos({
  contactos,
  cargando,
  alCambiar,
}: {
  contactos: readonly ContactoCliente[];
  cargando: boolean;
  alCambiar: (contactos: ContactoCliente[]) => void;
}) {
  const editar = (indice: number, cambio: Partial<ContactoCliente>) =>
    alCambiar(contactos.map((c, i) => (i === indice ? { ...c, ...cambio } : c)));

  const marcarPredeterminado = (indice: number) =>
    alCambiar(contactos.map((c, i) => ({ ...c, esPredeterminado: i === indice })));

  return (
    <section className="space-y-2 rounded-md border border-neutral-200 p-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-neutral-700 text-sm">Contactos</h3>
        <Boton
          variante="secundario"
          onClick={() =>
            alCambiar([
              ...contactos,
              {
                id: `nuevo-${contactos.length}-${contactos.length ? "" : "primero"}`,
                nombre: "",
                email: "",
                telefono: "",
                observacion: "",
                esPredeterminado: contactos.length === 0,
              },
            ])
          }
        >
          Agregar
        </Boton>
      </div>

      {cargando ? (
        <p className="text-neutral-500 text-sm">Cargando...</p>
      ) : contactos.length === 0 ? (
        <p className="text-neutral-500 text-sm">
          Sin contactos. Sin al menos uno no hay a quién mandarle el presupuesto.
        </p>
      ) : (
        <ul className="space-y-3">
          {contactos.map((contacto, indice) => (
            <li
              key={contacto.id}
              className="space-y-2 border-neutral-100 border-t pt-3 first:border-0 first:pt-0"
            >
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  aria-label={`Nombre del contacto ${indice + 1}`}
                  placeholder="Nombre"
                  value={contacto.nombre ?? ""}
                  onChange={(e) => editar(indice, { nombre: e.target.value })}
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
                />
                <input
                  aria-label={`Email del contacto ${indice + 1}`}
                  placeholder="Email"
                  type="email"
                  value={contacto.email ?? ""}
                  onChange={(e) => editar(indice, { email: e.target.value })}
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
                />
                <input
                  aria-label={`Teléfono del contacto ${indice + 1}`}
                  placeholder="Teléfono"
                  value={contacto.telefono ?? ""}
                  onChange={(e) => editar(indice, { telefono: e.target.value })}
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
                />
                <input
                  aria-label={`Observación del contacto ${indice + 1}`}
                  placeholder="Cargo, área u observación"
                  value={contacto.observacion ?? ""}
                  onChange={(e) => editar(indice, { observacion: e.target.value })}
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-neutral-600 text-sm">
                  <input
                    type="radio"
                    name="contacto-predeterminado"
                    checked={contacto.esPredeterminado}
                    onChange={() => marcarPredeterminado(indice)}
                  />
                  {/* `radio` y no `checkbox`: que sea uno solo es la regla, y el
                      control tiene que decirlo antes de que alguien lo pruebe. */}
                  Es el contacto principal
                </label>
                <Boton
                  variante="peligro"
                  onClick={() => {
                    const quedan = contactos.filter((_, i) => i !== indice);
                    // Si se fue el principal, el primero que queda lo hereda.
                    alCambiar(
                      quedan.some((c) => c.esPredeterminado)
                        ? quedan
                        : quedan.map((c, i) => ({ ...c, esPredeterminado: i === 0 })),
                    );
                  }}
                >
                  Quitar
                </Boton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
